import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { hostCardStyle } from './HostSettingsCard';
import { getNotificationActionPath } from '../utils/notificationRouting';
import { getListingPresentation } from '../utils/hostListings';

const font = "'Inria Serif', serif";

const formatGroupDate = (value) => new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
}).format(new Date(value));

const formatDisplayDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit' }).format(date);
};

// Both `detected` (legacy manual-approval flow) and `confirmed_by_host`
// (2026-02+ flow — iCal reservations skip the manual approval step and
// open directly as shareable) are freshly-detected iCal reservations
// that should render as the rich reservation card. Any legacy rows
// stored with raw-ISO body strings are ALSO fixed by this — the rich
// card builds its date line from `metadata.check_in_date`/`checkout_date`
// via `formatDisplayDate`, so the notification body string is never
// rendered verbatim.
const isReservationNotification = (item) => item?.metadata?.source === 'ical' && ['detected', 'confirmed_by_host'].includes(item?.metadata?.status);

const buildReservationDateLine = (item) => {
  const start = item?.metadata?.check_in_date;
  const end = item?.metadata?.checkout_date;
  return start && end ? `${formatDisplayDate(start)} — ${formatDisplayDate(end)}` : '';
};

export const NotificationHistoryCard = ({ notifications, dataTestIdPrefix, emptyCopy = 'No notifications from the last 30 days' }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const groupedNotifications = useMemo(() => notifications.reduce((groups, item) => {
    const key = formatGroupDate(item.created_at);
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(item);
    return groups;
  }, {}), [notifications]);

  return (
    <div data-testid={`${dataTestIdPrefix}-card`} style={{ ...hostCardStyle, width: '100%', padding: '14px 16px' }}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        data-testid={`${dataTestIdPrefix}-toggle-button`}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        <div style={{ textAlign: 'left' }}>
          <p style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Notifications History</p>
          <p style={{ fontFamily: font, fontSize: 13, color: 'var(--text-2)', margin: '4px 0 0' }}>Past 30 days only</p>
        </div>
        {open ? <ChevronUp size={20} color="var(--text-2)" /> : <ChevronDown size={20} color="var(--text-2)" />}
      </button>

      {open && (
        <div data-testid={`${dataTestIdPrefix}-content`} style={{ marginTop: 14, maxHeight: 240, overflowY: 'auto', paddingRight: 2 }}>
          {notifications.length === 0 ? (
            <div style={{ borderRadius: 18, background: 'var(--surface)', border: '1px solid rgba(112, 63, 246, 0.1)', padding: '14px 12px' }}>
              <p style={{ fontFamily: font, fontSize: 15, color: 'var(--text-2)', lineHeight: 1.4, margin: 0, textAlign: 'center' }}>{emptyCopy}</p>
            </div>
          ) : Object.entries(groupedNotifications).map(([date, items]) => (
            <div key={date} style={{ marginBottom: 14 }}>
              <p style={{ fontFamily: font, fontSize: 12, color: 'var(--text-2)', margin: '0 0 8px' }}>{date}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {items.map((item) => {
                  const actionPath = getNotificationActionPath(item);
                  const reservationMeta = getListingPresentation({ title: item?.metadata?.listing_name, address: item?.metadata?.listing_address });
                  const reservationImage = item?.metadata?.listing_image || '/allure-logo-mono.png';
                  const isDetected = ['detected', 'confirmed_by_host'].includes(item?.metadata?.status);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      data-testid={`${dataTestIdPrefix}-item-${item.id}`}
                      onClick={() => actionPath && navigate(actionPath)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        borderRadius: 18,
                        border: '1px solid rgba(112, 63, 246, 0.08)',
                        background: 'var(--surface)',
                        padding: '12px 14px',
                        cursor: actionPath ? 'pointer' : 'default',
                      }}
                    >
                      {isReservationNotification(item) ? (
                        <div style={{ display: 'grid', gap: 10 }}>
                          <p style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: isDetected ? 'var(--status-success-text)' : 'var(--text)', lineHeight: 1.15, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {isDetected ? 'Reservation detected' : item.title}
                          </p>
                          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            <div style={{ width: 58, height: 58, borderRadius: 18, overflow: 'hidden', background: 'var(--surface-2)', flexShrink: 0 }}>
                              <img src={reservationImage} alt={reservationMeta.displayName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                            <div style={{ minWidth: 0, display: 'grid', gap: 4, flex: 1 }}>
                              <p style={{ fontFamily: font, fontSize: 17, fontWeight: 700, color: 'var(--text)', lineHeight: 1.1, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {reservationMeta.displayName}
                              </p>
                              <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', lineHeight: 1.1, margin: 0 }}>
                                {reservationMeta.displayAddress || 'Location unavailable'}
                              </p>
                            </div>
                          </div>
                          <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', lineHeight: 1.2, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {buildReservationDateLine(item)}
                          </p>
                        </div>
                      ) : (
                        <>
                          <p style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--text)', lineHeight: 1.25, margin: 0 }}>{item.title}</p>
                          <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', lineHeight: 1.45, margin: '6px 0 0' }}>{item.message}</p>
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationHistoryCard;